import React, { useEffect, useState } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatPaise } from '@/lib/api';
import { topUp } from '@/lib/razorpay';
import TrustPanel from '@/components/TrustPanel';

const PRESETS = [50000, 100000, 250000, 500000];

export default function TopUpDialog({ open, onOpenChange, suggested, onPaid }) {
  const [amount, setAmount] = useState(Math.max(suggested || 100000, 50000));
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setAmount(Math.max(suggested || 100000, 50000)); setCustom(''); }
  }, [open, suggested]);
  const value = custom ? Math.round(parseFloat(custom) * 100) : amount;

  const pay = async () => {
    if (!value || value < 50000) { toast.error('The minimum top-up is ₹500.'); return; }
    setBusy(true);
    try {
      const balance = await topUp(value);
      if (balance !== null) {
        toast.success(`${formatPaise(value)} added to your wallet!`);
        onPaid?.(balance);
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
    setBusy(false);
  };

  const presets = [...new Set([...(suggested && !PRESETS.includes(suggested) ? [suggested] : []), ...PRESETS])].slice(0, 4);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="topup-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl font-extrabold">Add money to your wallet</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {presets.map(p => (
              <button key={p} onClick={() => { setAmount(p); setCustom(''); }}
                className={`p-3 rounded-xl border-2 text-base font-bold transition-all ${!custom && amount === p ? 'border-ink bg-butter shadow-neo-sm' : 'border-ink/25 hover:border-ink'}`}>
                {formatPaise(p)}
              </button>
            ))}
          </div>
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Or enter an amount (₹)</Label>
            <Input type="number" min={500} inputMode="numeric" value={custom} onChange={e => setCustom(e.target.value)} placeholder="e.g. 3000" data-testid="topup-custom-input" />
            <p className="text-xs text-muted-foreground mt-1.5">Minimum ₹500.</p>
          </div>
          <TrustPanel variant="compact" className="rounded-xl bg-muted/60 p-3" />
          <Button className="w-full gradient-bg h-12 text-base" onClick={pay} disabled={busy} data-testid="topup-pay-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Pay {formatPaise(value || 0)} securely
          </Button>
          <p className="text-[11px] text-center text-muted-foreground -mt-2">Payment handled by Razorpay · UPI, cards, net banking</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
